import { z } from "zod";
import type { ApiClient } from "../api/ApiClient";
import { ApiError } from "../api/errors";

/**
 * State of push notifications on this device:
 * - `unsupported`: the browser has no Web Push;
 * - `installRequired`: iOS offers Web Push only for apps added to the home screen;
 * - `notConfigured`: the server has no VAPID key;
 * - `denied`: the person blocked notifications in the browser;
 * - `off` / `on`: the device is not / is registered.
 */
export type PushState =
  "unsupported" | "installRequired" | "notConfigured" | "denied" | "off" | "on";

/** Subscription data as sent to the server (`PushSubscription.toJSON()`). */
export interface PushSubscriptionData {
  readonly endpoint: string;
  readonly keys: { readonly p256dh: string; readonly auth: string };
}

/** The part of a browser `PushSubscription` the controller uses. */
export interface SubscriptionPort {
  readonly endpoint: string;
  toJSON(): { readonly endpoint?: string; readonly keys?: Record<string, string> };
  unsubscribe(): Promise<boolean>;
}

/** The part of a browser `PushManager` the controller uses. */
export interface PushManagerPort {
  getSubscription(): Promise<SubscriptionPort | null>;
  subscribe(options: {
    readonly userVisibleOnly: true;
    readonly applicationServerKey: Uint8Array<ArrayBuffer>;
  }): Promise<SubscriptionPort>;
}

/** Browser capabilities for push notifications (injected for tests). */
export interface PushPlatform {
  /** Whether service workers, the Push API and notifications exist. */
  readonly supported: boolean;
  /** Whether the platform needs an installed app for push (iOS in a browser tab). */
  readonly requiresInstallation: boolean;
  /** Current notification permission. */
  permission(): NotificationPermission;
  /** Asks for the notification permission (needs a user gesture). */
  requestPermission(): Promise<NotificationPermission>;
  /** Returns the push manager of the active service worker registration. */
  pushManager(): Promise<PushManagerPort | null>;
}

/** Server calls of the push functions (injected for tests). */
export interface PushApi {
  /** Returns the VAPID public key, or `null` if push is not configured. */
  publicKey(): Promise<string | null>;
  register(subscription: PushSubscriptionData): Promise<void>;
  unregister(endpoint: string): Promise<void>;
}

/** What the UI needs from push notifications. */
export interface PushNotificationController {
  /** Determines the current state. */
  getState(): Promise<PushState>;
  /** Asks for permission, subscribes the device and registers it at the server. */
  enable(): Promise<PushState>;
  /** Removes the device at the server and unsubscribes it. */
  disable(): Promise<PushState>;
  /** Registers an existing subscription again (new login, changed roles). */
  refresh(): Promise<void>;
  /** Best-effort removal before sign-out; failures are ignored. */
  removeBeforeSignOut(): Promise<void>;
}

const publicKeySchema = z.object({ publicKey: z.string() });
const stateSchema = z.object({ registered: z.boolean() });

/**
 * Creates the server calls on top of the API client.
 *
 * @param client - The API client.
 * @returns The push API.
 */
export function createHttpPushApi(client: ApiClient): PushApi {
  const api: PushApi = {
    publicKey: async (): Promise<string | null> => {
      try {
        const response = await client.get("/api/v1/push-subscriptions/public-key", publicKeySchema);
        return response.publicKey;
      } catch (error) {
        if (error instanceof ApiError && error.code === "TT-3012") {
          return null;
        }

        throw error;
      }
    },
    register: async (subscription): Promise<void> => {
      await client.post("/api/v1/push-subscriptions", subscription, stateSchema);
    },
    unregister: async (endpoint): Promise<void> => {
      await client.post("/api/v1/push-subscriptions:unregister", { endpoint }, stateSchema);
    },
  };

  return api;
}

/**
 * Decodes a base64url string (VAPID key) into bytes.
 *
 * @param value - The encoded value.
 * @returns The bytes.
 */
export function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/gu, "+").replace(/_/gu, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

  return bytes;
}

/**
 * Converts a browser subscription into the server format.
 *
 * @param subscription - The subscription.
 * @returns The data, or `null` if keys are missing.
 */
export function toSubscriptionData(subscription: SubscriptionPort): PushSubscriptionData | null {
  const json = subscription.toJSON();
  const p256dh = json.keys?.["p256dh"];
  const auth = json.keys?.["auth"];
  const data =
    p256dh === undefined || auth === undefined
      ? null
      : { endpoint: subscription.endpoint, keys: { p256dh, auth } };

  return data;
}

/**
 * Push notifications of this device (T-071): opt-in per device, registration at the server,
 * removal on request and before sign-out (data minimization).
 */
export class WebPushNotificationController implements PushNotificationController {
  private readonly platform: PushPlatform;

  private readonly api: PushApi;

  /**
   * Creates the controller.
   *
   * @param platform - Browser capabilities.
   * @param api - Server calls.
   */
  constructor(platform: PushPlatform, api: PushApi) {
    this.platform = platform;
    this.api = api;
  }

  /** @inheritdoc */
  async getState(): Promise<PushState> {
    const precondition = this.precondition();

    if (precondition !== null) {
      return precondition;
    }

    const key = await this.api.publicKey();
    const subscription = key === null ? null : await this.currentSubscription();
    let state: PushState;

    if (key === null) {
      state = "notConfigured";
    } else if (this.platform.permission() === "denied") {
      state = "denied";
    } else {
      state = subscription !== null && this.platform.permission() === "granted" ? "on" : "off";
    }

    return state;
  }

  /** @inheritdoc */
  async enable(): Promise<PushState> {
    const precondition = this.precondition();

    if (precondition !== null) {
      return precondition;
    }

    const key = await this.api.publicKey();

    if (key === null) {
      return "notConfigured";
    }

    const permission = await this.platform.requestPermission();

    if (permission !== "granted") {
      const refused: PushState = permission === "denied" ? "denied" : "off";
      return refused;
    }

    const manager = await this.platform.pushManager();

    if (manager === null) {
      return "unsupported";
    }

    const subscription =
      (await manager.getSubscription()) ??
      (await manager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeBase64Url(key),
      }));
    const data = toSubscriptionData(subscription);

    if (data === null) {
      await subscription.unsubscribe();
      return "unsupported";
    }

    await this.api.register(data);
    const state: PushState = "on";

    return state;
  }

  /** @inheritdoc */
  async disable(): Promise<PushState> {
    const subscription = await this.currentSubscription();

    if (subscription !== null) {
      await this.api.unregister(subscription.endpoint);
      await subscription.unsubscribe();
    }

    const state: PushState = "off";

    return state;
  }

  /** @inheritdoc */
  async refresh(): Promise<void> {
    if (this.precondition() !== null || this.platform.permission() !== "granted") {
      return;
    }

    const subscription = await this.currentSubscription();
    const data = subscription === null ? null : toSubscriptionData(subscription);

    if (data !== null) {
      await this.api.register(data);
    }
  }

  /** @inheritdoc */
  async removeBeforeSignOut(): Promise<void> {
    try {
      await this.disable();
    } catch {
      // Offline or session already gone: the server removes the device once the push service
      // reports it as gone; the next person on this device registers it anew.
    }
  }

  private precondition(): PushState | null {
    let state: PushState | null = null;

    if (this.platform.requiresInstallation) {
      state = "installRequired";
    } else if (!this.platform.supported) {
      state = "unsupported";
    }

    return state;
  }

  private async currentSubscription(): Promise<SubscriptionPort | null> {
    const manager = this.precondition() === null ? await this.platform.pushManager() : null;
    const subscription = manager === null ? null : await manager.getSubscription();

    return subscription;
  }
}

/**
 * Creates a controller for environments without service worker (development server, tests).
 *
 * @returns The controller; push is always unsupported.
 */
export function createUnsupportedPushController(): PushNotificationController {
  const controller: PushNotificationController = {
    getState: () => Promise.resolve("unsupported"),
    enable: () => Promise.resolve("unsupported"),
    disable: () => Promise.resolve("unsupported"),
    refresh: () => Promise.resolve(),
    removeBeforeSignOut: () => Promise.resolve(),
  };

  return controller;
}
